import { PkButton, PkIcon, PkPopover } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useEffect, useState } from 'react';

import { useWindowSize } from '../hooks/useWindowSize';
import { useTranslateFn } from '../Translate';

import { AppPromoType } from '@shared-types/promo.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

import * as styles from './styles.module.scss';

const NotificationButton = memo(
  ({
    selector,
    mode,
    disableButtonText,
    setHover,
  }: {
    selector?: string;
    mode?: AppPromoType;
    disableButtonText?: boolean;
    setHover?: (state: boolean) => void;
  }) => {
    const t = useTranslateFn();
    const draw = mode === AppPromoType.DRAW;

    const handleClick = () => {
      const pkBottomPanel = document.querySelector<HTMLPkBottomPanelElement>(selector);

      if (!pkBottomPanel) return;

      const eventAction = ['Notifications', mode?.toLowerCase()?.replace(/^\w/, (c) => c.toUpperCase())]
        .filter(Boolean)
        .join(' - ');
      GoogleTagManagerHelper.event(
        'Click',
        eventAction,
        'app download',
        { section_reference: 'app download' },
        'panel_dialog_main_action',
      );
      document.querySelectorAll('pk-bottom-panel').forEach((e) => e.close());
      pkBottomPanel.open();
    };

    return (
      <PkButton
        className={clsx({ [styles.promoAppNotificationButton]: !draw })}
        onPkClick={handleClick}
        appearance='outlined'
        slot='trigger'
        width='auto'
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {!disableButtonText && (
          <span className={clsx({ [styles.buttonText]: !draw })}>{t(draw ? 'notify_me' : 'notifications')}</span>
        )}
        {!draw && (
          <PkIcon className={clsx(styles.buttonIcon, !disableButtonText && 'pk-ml-md--s')} name='notifications-none' />
        )}
      </PkButton>
    );
  },
);
NotificationButton.displayName = 'NotificationButton';

const PromoAppNotificationsBtn = ({
  selector,
  mode,
  bottomPanelSelector,
  promoCssClass,
  promoOpeningTime,
  disablePromoPopover,
  disableButtonText,
}: {
  selector?: string;
  mode?: AppPromoType;
  bottomPanelSelector: string;
  promoCssClass?: string;
  promoOpeningTime?: number;
  disablePromoPopover?: boolean;
  disableButtonText?: boolean;
}) => {
  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [hoverOnBtn, setHoverOnBtn] = useState(false);
  const panelSelector = selector ?? bottomPanelSelector;
  const pkBottomPanel = document.querySelector<HTMLPkBottomPanelElement>(panelSelector);

  useEffect(() => {
    if (!promoOpeningTime) return;

    const timeout = setTimeout(() => {
      setPopoverOpen(true);
    }, promoOpeningTime);

    return () => {
      clearTimeout(timeout);
    };
  }, [promoOpeningTime]);

  if (!pkBottomPanel) return null;

  return (
    <div className={clsx(promoCssClass)}>
      {disablePromoPopover || mode === AppPromoType.DRAW ? (
        <NotificationButton selector={panelSelector} mode={mode} disableButtonText={disableButtonText} />
      ) : (
        <PkPopover
          className={clsx(styles.promoAppNotificationPopover, hoverOnBtn && styles.hoverBtn)}
          open={popoverOpen}
          placement={isMobile ? 'bottom-start' : 'left'}
          noCloseButton
          disableTriggerClick
          distance={12}
          autodismiss
          dismissTime={10}
        >
          <NotificationButton selector={panelSelector} mode={mode} setHover={setHoverOnBtn} />

          <div className='pk-d--flex pk-justify-content--between pk-text--text-04'>
            {t('app_promo_notification')}
            <PkIcon
              className={styles.promoAppNotificationPopoverIcon}
              onClick={() => setPopoverOpen(false)}
              name='navigation-close'
              size='20'
              color='ui-03'
            />
          </div>
        </PkPopover>
      )}
    </div>
  );
};

export default memo(PromoAppNotificationsBtn);
